import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ChicagoToIreland() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8af26e7cea344956a95f85ad32910795.jpg"
            alt="Chicago to Dublin relocation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Moving to Ireland from Chicago</h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90">Your complete guide to relocating from the Windy City to the Emerald Isle</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/tools/cost-calculator"
              className="px-8 py-4 bg-[#D4AF37] hover:bg-[#C19B2F] text-white rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Compare Costs
            </Link>
            <Link
              to="/directory"
              className="px-8 py-4 bg-white hover:bg-gray-100 text-[#0A381F] rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Find Services
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-flight-takeoff-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">8-9 hours</div>
              <div className="text-white/80">Direct flight time</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-[#D4AF37] to-[#C19B2F] rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-time-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">+6 hours</div>
              <div className="text-white/80">Time difference</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">30-40%</div>
              <div className="text-white/80">Lower housing costs</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-green-600 to-green-700 rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-building-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">500+</div>
              <div className="text-white/80">US companies in Ireland</div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Chicago vs Dublin: Cost Comparison</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            See how your Chicago expenses translate to Dublin living costs
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Housing Costs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Housing Costs</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">1-Bedroom Apartment (City Center)</div>
                    <div className="text-sm text-gray-500">Chicago Loop vs Dublin City Centre</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$2,200</div>
                    <div className="text-sm text-green-600">vs $1,800</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">2-Bedroom Apartment (City Center)</div>
                    <div className="text-sm text-gray-500">Downtown Chicago vs Dublin 2</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$3,200</div>
                    <div className="text-sm text-green-600">vs $2,600</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">3-Bedroom House (Suburbs)</div>
                    <div className="text-sm text-gray-500">Naperville vs Malahide</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$2,800</div>
                    <div className="text-sm text-green-600">vs $2,400</div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-green-700 font-semibold">
                    <i className="ri-arrow-down-line"></i>
                    <span>Save $400-600/month on housing in Dublin</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Transportation */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Transportation</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Monthly Transit Pass</div>
                    <div className="text-sm text-gray-500">CTA vs Dublin Bus/DART</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$105</div>
                    <div className="text-sm text-red-600">vs $140</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Gas (per gallon)</div>
                    <div className="text-sm text-gray-500">Petrol prices</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$3.50</div>
                    <div className="text-sm text-red-600">vs $6.80</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Car Insurance (Annual)</div>
                    <div className="text-sm text-gray-500">Full coverage</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$1,400</div>
                    <div className="text-sm text-red-600">vs $1,800</div>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-blue-700 font-semibold">
                    <i className="ri-information-line"></i>
                    <span>Most Dubliners don't need cars - excellent public transit</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Costs Table */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Daily Living Expenses</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Item</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Chicago</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Dublin</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-4 px-4">Meal at Restaurant</td>
                    <td className="text-right py-4 px-4">$18</td>
                    <td className="text-right py-4 px-4">$20</td>
                    <td className="text-right py-4 px-4 text-red-600">+11%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Coffee (Starbucks)</td>
                    <td className="text-right py-4 px-4">$5</td>
                    <td className="text-right py-4 px-4">$4.50</td>
                    <td className="text-right py-4 px-4 text-green-600">-10%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Groceries (Monthly)</td>
                    <td className="text-right py-4 px-4">$400</td>
                    <td className="text-right py-4 px-4">$450</td>
                    <td className="text-right py-4 px-4 text-red-600">+13%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Gym Membership</td>
                    <td className="text-right py-4 px-4">$50</td>
                    <td className="text-right py-4 px-4">$55</td>
                    <td className="text-right py-4 px-4 text-red-600">+10%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Internet (Monthly)</td>
                    <td className="text-right py-4 px-4">$60</td>
                    <td className="text-right py-4 px-4">$50</td>
                    <td className="text-right py-4 px-4 text-green-600">-17%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Movie Ticket</td>
                    <td className="text-right py-4 px-4">$14</td>
                    <td className="text-right py-4 px-4">$12</td>
                    <td className="text-right py-4 px-4 text-green-600">-14%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Jobs & Career */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Jobs for Chicagoans in Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Chicago's strong finance and tech sectors translate well to Ireland's booming economy
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-building-4-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Chicago Companies in Ireland</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Boeing:</strong> European headquarters in Dublin</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Abbott Laboratories:</strong> Major operations in Longford and Donegal</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Walgreens Boots Alliance:</strong> Boots stores nationwide</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Motorola Solutions:</strong> Cork office</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>United Airlines:</strong> Shannon operations</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#D4AF37] to-[#C19B2F] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-briefcase-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Top Industries for Chicagoans</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-line text-white text-xl mt-1"></i>
                  <span><strong>Financial Services:</strong> Dublin's IFSC rivals Chicago's financial district</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-line text-white text-xl mt-1"></i>
                  <span><strong>Technology:</strong> Google, Meta, Microsoft all hiring</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-line text-white text-xl mt-1"></i>
                  <span><strong>Pharmaceuticals:</strong> Pfizer, Johnson &amp; Johnson, Merck</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-line text-white text-xl mt-1"></i>
                  <span><strong>Aviation:</strong> Shannon and Dublin airports expanding</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-line text-white text-xl mt-1"></i>
                  <span><strong>Healthcare:</strong> Medical device manufacturing hub</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Salary Comparison */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Salary Expectations for Chicagoans</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="text-sm text-gray-600 mb-2">Software Engineer</div>
                <div className="text-2xl font-bold text-[#0A381F] mb-1">$95,000</div>
                <div className="text-sm text-gray-500">Chicago: $110,000</div>
                <div className="text-sm text-green-600 mt-2">+ Better work-life balance</div>
              </div>
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="text-sm text-gray-600 mb-2">Financial Analyst</div>
                <div className="text-2xl font-bold text-[#0A381F] mb-1">$70,000</div>
                <div className="text-sm text-gray-500">Chicago: $80,000</div>
                <div className="text-sm text-green-600 mt-2">+ 25 vacation days standard</div>
              </div>
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="text-sm text-gray-600 mb-2">Marketing Manager</div>
                <div className="text-2xl font-bold text-[#0A381F] mb-1">$65,000</div>
                <div className="text-sm text-gray-500">Chicago: $75,000</div>
                <div className="text-sm text-green-600 mt-2">+ Healthcare included</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lifestyle Comparison */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Lifestyle: Chicago vs Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            What Chicagoans love (and miss) about living in Ireland
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* What You'll Love */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-heart-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A381F]">What You'll Love</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">No Brutal Winters:</strong>
                    <p className="text-gray-600">Ireland's mild climate means no more -20°F polar vortexes or shoveling snow</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Walkable Cities:</strong>
                    <p className="text-gray-600">Dublin is compact and pedestrian-friendly, unlike sprawling Chicago</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">European Travel:</strong>
                    <p className="text-gray-600">$50 flights to Paris, Barcelona, Rome - weekend trips are easy</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Work-Life Balance:</strong>
                    <p className="text-gray-600">25+ vacation days, shorter commutes, less hustle culture</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Pub Culture:</strong>
                    <p className="text-gray-600">Authentic Irish pubs everywhere (not just on St. Patrick's Day!)</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Healthcare:</strong>
                    <p className="text-gray-600">Save $5,000-15,000/year on health insurance and medical costs</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* What You'll Miss */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-full">
                  <i className="ri-emotion-sad-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A381F]">What You'll Miss</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Deep Dish Pizza:</strong>
                    <p className="text-gray-600">Ireland has pizza, but nothing like Lou Malnati's or Giordano's</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Chicago Sports:</strong>
                    <p className="text-gray-600">Watching Cubs, Bears, Bulls, Blackhawks means late-night streaming</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Sunny Summers:</strong>
                    <p className="text-gray-600">Ireland is rainy and overcast - you'll miss those perfect Chicago summer days</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">24/7 Everything:</strong>
                    <p className="text-gray-600">Shops close early, no 24-hour diners or late-night Portillo's runs</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Mexican Food:</strong>
                    <p className="text-gray-600">Chicago's amazing Mexican restaurants don't exist in Ireland</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Lake Michigan:</strong>
                    <p className="text-gray-600">Ireland has beautiful coastline, but nothing like Chicago's lakefront</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Tips */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-12 text-center">Practical Tips for Chicagoans</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-flight-takeoff-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Direct Flights</h3>
              <p className="text-white/90 mb-4">Aer Lingus and United fly direct from O'Hare to Dublin (8-9 hours). Book early for best prices ($400-800 round trip).</p>
              <p className="text-sm text-white/80">Tip: Red-eye flights arrive in Dublin morning - perfect for beating jet lag</p>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-bank-card-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Banking</h3>
              <p className="text-white/90 mb-4">Open a Revolut or Wise account before leaving Chicago. Avoid Chase/Bank of America international fees.</p>
              <p className="text-sm text-white/80">Tip: Keep your US bank account open for credit history</p>
            </div>

            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-phone-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Phone Number</h3>
              <p className="text-white/90 mb-4">Port your Chicago number to Google Voice (free). Get an Irish SIM from Three or Vodafone on arrival.</p>
              <p className="text-sm text-white/80">Tip: Keep US number for 2FA and banking</p>
            </div>
          </div>
        </div>
      </section>

      {/* Relocation Expert CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#0A381F] to-[#0D4A28]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-customer-service-2-line text-4xl"></i>
          </div>
          <h2 className="text-4xl font-bold mb-6">Questions About Moving from Chicago?</h2>
          <p className="text-xl text-white/90 mb-8">
            Our Ireland Relocation Expert can answer all your questions about visas, housing, jobs, and life in Ireland
          </p>
          <button
            onClick={() => {
              const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
              if (widget) widget.click();
            }}
            className="px-8 py-4 bg-[#D4AF37] hover:bg-[#C19B2F] text-white rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-3"
          >
            <i className="ri-chat-3-line text-xl"></i>
            <span>Talk to Relocation Expert</span>
          </button>
        </div>
      </section>

      {/* Chicago Expat Community */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Chicago Expat Community in Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Connect with fellow Chicagoans who've made the move
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Where Chicagoans Settle</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-[#D4AF37] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Dublin 2 &amp; 4:</strong>
                    <p className="text-gray-600">Similar vibe to Lincoln Park - young professionals, restaurants, nightlife</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-[#D4AF37] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Malahide:</strong>
                    <p className="text-gray-600">Family-friendly suburb like Evanston - great schools, coastal location</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-[#D4AF37] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Cork City:</strong>
                    <p className="text-gray-600">Smaller city feel like Naperville - tech jobs, lower cost of living</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Connect with Chicagoans</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-group-line text-[#D4AF37] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Americans in Ireland Facebook Group:</strong>
                    <p className="text-gray-600">15,000+ members, many from Chicago area</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-group-line text-[#D4AF37] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Chicago Cubs Supporters Club Dublin:</strong>
                    <p className="text-gray-600">Watch games together at pubs around Dublin</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-group-line text-[#D4AF37] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">InterNations Dublin:</strong>
                    <p className="text-gray-600">Monthly expat meetups and networking events</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-6">Ready to Make the Move?</h2>
          <p className="text-xl text-gray-600 mb-12">
            Start your relocation journey with our comprehensive tools and resources
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/tools/visa-quiz"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#0A381F] rounded-full mx-auto mb-4">
                <i className="ri-questionnaire-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Check Visa Eligibility</h3>
              <p className="text-gray-600 text-sm">Find out which visa you qualify for</p>
            </Link>

            <Link
              to="/tools/cost-calculator"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                <i className="ri-calculator-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Calculate Your Budget</h3>
              <p className="text-gray-600 text-sm">Compare Chicago vs Dublin costs</p>
            </Link>

            <Link
              to="/directory"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-purple-600 rounded-full mx-auto mb-4">
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Find Services</h3>
              <p className="text-gray-600 text-sm">Immigration lawyers, movers, and more</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
